"use client";

import type { DepictFilter, DepictSortOption, FilterData } from "@/lib/depict/types";
import { useToken } from "@frend-digital/centra/client";
import { createContext, type PropsWithChildren, use, useMemo } from "react";
import { withSearchParams } from "@/components/withSearchParams";
import type { DepictOptions } from "@frend-digital/depict/server";
import type { ProductListData } from "./actions";
import { useListingOptions, useProductListingQuery } from "./hooks";

export interface ProductListContextType {
	filterKeys: string[];
	initialData?: ProductListData;
	searchParams: URLSearchParams;
	options: DepictOptions;
	query: ReturnType<typeof useProductListingQuery>;
	total: number;
	appliedFilters: FilterData[];
	sorts?: DepictSortOption[];
	filters?: DepictFilter[];
	parentHref: string;
	firstPage: ProductListData | undefined;
	lastPage: ProductListData | undefined;
	isLoading: boolean;
}

export const ProductsContext = createContext<ProductListContextType>(null!);

type ListingProps = PropsWithChildren<
	Pick<ProductListContextType, "filterKeys" | "options" | "initialData">
> & {
	depictCategory: string | undefined;
	parentHref: string;
};

export const ProductsProvider = withSearchParams<ListingProps>(
	({
		filterKeys,
		initialData,
		children,
		searchParams,
		options: serverOptions,
		depictCategory,
		parentHref,
	}) => {
		const token = useToken();

		const [options] = useListingOptions({
			serverOptions,
			filterKeys,
			searchParams,
			token,
		});

		const query = useProductListingQuery({
			initialData,
			filterKeys,
			serverOptions: options,
			searchParams: searchParams,
			depictCategory,
		});

		const isLoading = !query.data && !query.error;

		const pages = query.data?.pages || [];

		const total = pages.length > 0 ? (pages[pages.length - 1]?.total ?? 0) : 0;

		const sorts = pages.length > 0 ? pages[0]?.sorts || [] : [];

		const filters = pages.length > 0 ? pages[pages.length - 1]?.filter || [] : [];

		const firstPage = pages.length > 0 ? pages[0] : undefined;
		const lastPage = pages.length > 0 ? pages[pages.length - 1] : undefined;

		return (
			<ProductsContext
				value={{
					firstPage,
					lastPage,
					filterKeys,
					initialData,
					searchParams,
					options,
					query,
					total,
					appliedFilters: (options.filters as unknown as FilterData[]) || [],
					sorts: sorts as DepictSortOption[],
					filters: filters as DepictFilter[],
					parentHref,
					isLoading,
				}}
			>
				{children}
			</ProductsContext>
		);
	},
);

export const useProducts = () => {
	const ctx = use(ProductsContext);

	if (!ctx) {
		throw new Error("useProductListContext must be used within a ProductListContextProvider");
	}

	return ctx;
};
